import FilterAltIcon from "@mui/icons-material/FilterAlt";
import * as React from "react";

import { FieldPicker } from "./FieldPicker";
import type { FilterDefinition } from "./FilterDefinition";
import { FilterForm } from "./FilterForm";
import styles from "./FilterMenuBody.module.css";
import type { Filter } from "./FilterSchema";

interface FilterMenuBodyProps {
  filterDefinitions: FilterDefinition[];
  /** Called when the user clicks Add filter. */
  onCommit: (filter: Filter) => void;
  /** Called when the user clicks Cancel. */
  onCancel: () => void;
}

/**
 * Body of the filter menu — layout shell + field-selection coordinator.
 * Mounts when the popover opens and unmounts when it closes (MUI Popover's
 * default behavior with `keepMounted={false}`), so every open starts with
 * `selectedField = null`.
 *
 * All form state (operator, values, validity, submit) lives one level down
 * in `FilterForm`, which only mounts after a field has been picked. The
 * `key={selectedField.field}` remounts `FilterForm` cleanly when the user
 * switches fields — fresh useForm with fresh defaults, no imperative reset
 * choreography.
 */
export function FilterMenuBody({
  filterDefinitions,
  onCommit,
  onCancel,
}: FilterMenuBodyProps) {
  const [selectedField, setSelectedField] =
    React.useState<FilterDefinition | null>(null);

  return (
    <div className={styles.popoverShell}>
      <div className={styles.popoverBody}>
        <FieldPicker
          filterDefinitions={filterDefinitions}
          onSelect={setSelectedField}
          selectedField={selectedField}
        />
        {selectedField === null ? (
          <FilterFormEmpty />
        ) : (
          <FilterForm
            // Reset form when selectedField changes
            key={selectedField.field}
            selectedField={selectedField}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
}

function FilterFormEmpty() {
  return (
    <div className={styles.emptyPanel}>
      <div className={styles.empty}>
        <div className={styles.emptyIcon} aria-hidden="true">
          <FilterAltIcon fontSize="medium" />
        </div>
        <p className={styles.emptyTitle}>Pick a field to filter on</p>
        <p className={styles.emptyBody}>
          Choose a field, set an operator, and enter a value.
        </p>
      </div>
    </div>
  );
}
